import { SupportedLanguage } from '@i18n/index'
import { useLanguage } from '@i18n/useLanguage'
import { match } from 'ts-pattern'

const useDateFormat = (
  isOrdinalGlobal?: boolean,
  includeTimeGlobal?: boolean,
) => {
  const [language] = useLanguage()
  const formatter = (
    date: Date,
    isOrdinalLocal?: boolean,
    includeTimeLocal?: boolean,
  ): string => {
    const includeTime = includeTimeLocal ?? includeTimeGlobal ?? false

    if (!date) {
      return ''
    }

    const isCurrentYear = date.getFullYear() === new Date().getFullYear()

    const dateOptions: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      ...(!isCurrentYear && { year: 'numeric' }),
    }

    const fullLocale = match(language)
      .with('en', () => 'en-GB')
      .otherwise(() => 'nl-NL')

    let formatted = new Intl.DateTimeFormat(fullLocale, dateOptions).format(
      date,
    )

    const isOrdinal = isOrdinalLocal ?? isOrdinalGlobal ?? false
    if (isOrdinal) {
      const formattedParts = formatted.split(' ')
      const ordinal = getOrdinalSuffix(date.getDate(), language)
      formattedParts[0] += ordinal
      formatted = formattedParts.join(' ')
    }

    if (includeTime) {
      const timeStr = new Intl.DateTimeFormat(fullLocale, {
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
      const connector = language === 'nl' ? 'om' : 'at'
      formatted = `${formatted} ${connector} ${timeStr}`
    }

    return formatted
  }

  return formatter
}

const getOrdinalSuffix = (day: number, language: SupportedLanguage) => {
  if (language === 'nl') {
    return ''
  }

  if ([11, 12, 13].includes(day)) {
    return 'th'
  }
  const lastDigit = day % 10
  if (lastDigit === 1) {
    return 'st'
  }
  if (lastDigit === 2) {
    return 'nd'
  }
  if (lastDigit === 3) {
    return 'rd'
  }
  return 'th'
}

export default useDateFormat
